/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/*
 * Hover information for charts that are not recharts (sparklines, bars, stacked bars): the same `.gh-tip`
 * surface as ChartTooltip (styles/tooltips.css), floated next to the pointer through a portal so no card
 * with `overflow: hidden` clips it. Works with a finger too (pointer events).
 *
 *   const tip = useHoverTip();
 *   <div onPointerMove={(e) => tip.show(e.clientX, e.clientY, <TipBody head="12:00" rows={[...]} />)} onPointerLeave={tip.hide}>
 *     ...
 *     {tip.node}
 *   </div>
 *
 * or, for one element: <HoverTip content={<TipBody .../>}>{element}</HoverTip>
 */

export interface TipRow {
  color?: string;
  name?: React.ReactNode;
  value: React.ReactNode;
}

/** The inside of a tooltip: an optional muted head line and rows (colour key, name, strong value). */
export const TipBody: React.FC<{ head?: React.ReactNode; rows?: TipRow[]; children?: React.ReactNode }> = ({ head, rows, children }) => (
  <>
    {head !== undefined && head !== null && head !== "" && <div className="gh-tip-head">{head}</div>}
    {rows?.map((r, i) => (
      <div key={i} className="gh-tip-row">
        {r.color ? <i style={{ background: r.color }} /> : null}
        {r.name !== undefined ? <span>{r.name}</span> : <span />}
        <b>{r.value}</b>
      </div>
    ))}
    {children}
  </>
);

const MARGIN = 8;

const FloatingTip: React.FC<{ x: number; y: number; children: React.ReactNode }> = ({ x, y, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Placed after layout, straight on the element: above and to the right of the pointer, flipped when it would leave the window.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = x + 14;
    if (left + w > window.innerWidth - MARGIN) left = Math.max(MARGIN, x - w - 14);
    let top = y - h - 14;
    if (top < MARGIN) top = Math.min(window.innerHeight - h - MARGIN, y + 20);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = "visible";
  });
  return createPortal(
    <div ref={ref} className="gh-tip" role="status" style={{ position: "fixed", left: 0, top: 0, zIndex: 200, visibility: "hidden" }}>
      {children}
    </div>,
    document.body,
  );
};

export function useHoverTip() {
  const [state, setState] = useState<{ x: number; y: number; content: React.ReactNode } | null>(null);
  const show = useCallback((x: number, y: number, content: React.ReactNode) => setState({ x, y, content }), []);
  const hide = useCallback(() => setState(null), []);
  const node = state ? (
    <FloatingTip x={state.x} y={state.y}>
      {state.content}
    </FloatingTip>
  ) : null;
  return { show, hide, node };
}

/** Wraps any element(s): hovering (or touching) them shows `content`. Adds no box of its own. */
export const HoverTip: React.FC<{ content: React.ReactNode; children: React.ReactNode }> = ({ content, children }) => {
  const tip = useHoverTip();
  return (
    <span
      style={{ display: "contents" }}
      onPointerMove={(e) => tip.show(e.clientX, e.clientY, content)}
      onPointerDown={(e) => tip.show(e.clientX, e.clientY, content)}
      onPointerLeave={tip.hide}
      onPointerCancel={tip.hide}
    >
      {children}
      {tip.node}
    </span>
  );
};
